import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/Button';
import { SignupContactPath, SignupHeader } from './SignupLayout';

/**
 * The screens that end self-serve signup without a checkout. None of them offers one: an unverified
 * or unavailable school goes to the demo path, and a program that already exists goes to its admin.
 */
export type SignupOutcomeState =
  | { phase: 'alreadyExists'; email?: string }
  | { phase: 'unverified' }
  | { phase: 'unavailable' }
  | {
      phase: 'signInInstead';
      email: string;
      message: string | null;
      /**
       * The address has a Firebase account whose password did not match: most often this coach's own
       * earlier, unfinished signup. Adds the "use the password you chose, or reset it" help.
       */
      passwordHelp?: boolean;
    };

const StartOverButton = ({ onStartOver }: { onStartOver: () => void }) => (
  <button
    type="button"
    className="signup-text-button"
    onClick={onStartOver}
    data-testid="signup-start-over"
  >
    Try a different email
  </button>
);

/**
 * "Sign in instead", as a button rather than a plain link: by now a Firebase session may exist for
 * the address, and `/login` bounces a signed-in user onward (a profile-less one straight back to
 * signup). Signing out first (via AuthContext, which also clears per-user caches and the stored
 * form) lands them on the sign-in form with the address filled in.
 */
const SignInInsteadButton = ({ email }: { email?: string }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const handleClick = async () => {
    setBusy(true);
    try {
      // Unconditionally: AuthContext's user lags behind Firebase (it waits on a profile request),
      // so "no user" there does not mean no session. Signing out with none is harmless.
      await logout();
    } catch {
      // A failed sign-out still leaves /login reachable; it will simply route them on.
    } finally {
      setBusy(false);
      navigate('/login', { state: email ? { email } : undefined });
    }
  };

  return (
    <Button
      type="button"
      fullWidth
      loading={busy}
      onClick={() => void handleClick()}
      data-testid="signup-sign-in-instead"
    >
      Go to sign in
    </Button>
  );
};

export const SignupOutcome = ({
  outcome,
  onStartOver,
}: {
  outcome: SignupOutcomeState;
  onStartOver: () => void;
}) => {
  if (outcome.phase === 'alreadyExists') {
    return (
      <>
        <SignupHeader title="Your program is already on RosterPilot" />
        <div className="accept-invite-message accept-invite-message--warning">
          <p>Your program already uses RosterPilot. Ask your program&apos;s admin to invite you.</p>
        </div>
        <div className="accept-invite-actions">
          <SignInInsteadButton email={outcome.email} />
          <StartOverButton onStartOver={onStartOver} />
        </div>
      </>
    );
  }

  if (outcome.phase === 'unverified') {
    return (
      <>
        <SignupHeader title="We could not verify your school" />
        <div className="accept-invite-message accept-invite-message--warning">
          <p>We couldn&apos;t verify your school from that email address.</p>
          <p>Schedule a demo and we will get your program set up.</p>
        </div>
        <SignupContactPath>
          <StartOverButton onStartOver={onStartOver} />
        </SignupContactPath>
      </>
    );
  }

  if (outcome.phase === 'unavailable') {
    return (
      <>
        <SignupHeader title="Online signup is unavailable" />
        <div className="accept-invite-message accept-invite-message--warning">
          <p>Self-serve signup isn&apos;t available right now.</p>
          <p>Schedule a demo and we will get your program set up.</p>
        </div>
        <SignupContactPath />
      </>
    );
  }

  return (
    <>
      <SignupHeader title="You already have an account" />
      <div className="accept-invite-message accept-invite-message--warning">
        <p>
          {outcome.message ??
            'An account with this email address already exists. Sign in instead.'}
        </p>
        {outcome.passwordHelp && (
          <p data-testid="signup-password-help">
            If you started signing up earlier, use the password you chose when you started, or{' '}
            <Link to="/forgot-password" className="accept-invite-link">
              reset it
            </Link>
            .
          </p>
        )}
      </div>
      <div className="accept-invite-actions">
        <SignInInsteadButton email={outcome.email} />
      </div>
    </>
  );
};
